/**
 * AuthGuard Component
 *
 * Protects routes by checking authentication status via backend /auth/me.
 * Redirects to auth service if user is not authenticated.
 */

import { useEffect, useState } from 'react';

import { AuthService } from '../services/auth-service';
import { AUTH_ORIGIN } from '../utils/domain';

const AUTH_URL = AUTH_ORIGIN;

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Loading component while checking authentication
 */
const AuthCheckLoader = (): React.ReactElement => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
      <p className="text-foreground-secondary">Verifying authentication...</p>
    </div>
  </div>
);

/**
 * AuthGuard - Calls backend /auth/me to check authentication.
 *
 * The backend reads the HttpOnly access_token cookie and returns user info.
 * If not authenticated, redirects to auth service with return URL.
 */
export function AuthGuard({ children }: AuthGuardProps): React.ReactElement {
  const [isChecking, setIsChecking] = useState(true);
  const [shouldShowContent, setShouldShowContent] = useState(false);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const startTime = Date.now();
      const MIN_DISPLAY_TIME = 800;

      const user = await AuthService.fetchMe();

      if (!user) {
        const returnUrl = encodeURIComponent(window.location.href);
        const authLoginUrl = `${AUTH_URL}/login?return_url=${returnUrl}`;
        await new Promise(resolve => setTimeout(resolve, 300));
        window.location.href = authLoginUrl;
        return;
      }

      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setIsChecking(false);
      setTimeout(() => {
        setShouldShowContent(true);
      }, 50);
    };

    void checkAuth();
  }, []);

  if (isChecking) {
    return <AuthCheckLoader />;
  }

  return (
    <div
      className={`transition-opacity duration-300 ${
        shouldShowContent ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {children}
    </div>
  );
}
